import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { adminApi } from '../services/api'
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Building,
  Users,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Pause
} from 'lucide-react'

// Status Badge per Organization
const StatusBadge = ({ status }) => {
  const config = {
    active: { 
      style: 'bg-green-100 text-green-700', 
      icon: CheckCircle,
      label: 'Attiva'
    },
    suspended: { 
      style: 'bg-yellow-100 text-yellow-700', 
      icon: Pause,
      label: 'Sospesa'
    },
    pending_deletion: { 
      style: 'bg-red-100 text-red-700', 
      icon: Clock,
      label: 'In Cancellazione'
    },
    deleted: { 
      style: 'bg-gray-100 text-gray-700', 
      icon: XCircle,
      label: 'Eliminata'
    }
  }

  const { style, icon: Icon, label } = config[status] || config.active

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${style}`}>
      <Icon className="h-3 w-3 mr-1" />
      {label}
    </span>
  )
}

// Card statistiche
const StatsCard = ({ title, value, icon: Icon, color = 'primary' }) => {
  const colors = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4">
      <div className={`p-3 rounded-lg ${colors[color]}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  )
}

export default function OrganizationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [organizations, setOrganizations] = useState([])
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState(null)

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [status, setStatus] = useState(searchParams.get('status') || '')

  useEffect(() => {
    loadOrganizations()
    loadStats()
  }, [searchParams])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      const params = {
        page: searchParams.get('page') || 1,
        limit: 20,
        search: searchParams.get('search') || undefined,
        status: searchParams.get('status') || undefined
      }

      const response = await adminApi.getOrganizations(params)
      setOrganizations(response.data.data.organizations)
      setPagination(response.data.data.pagination)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const response = await adminApi.getOrganizationsOverview()
      setStats(response.data.data)
    } catch (err) {
      console.error('Error loading stats:', err)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    const params = new URLSearchParams(searchParams)
    if (search) params.set('search', search)
    else params.delete('search')
    if (status) params.set('status', status)
    else params.delete('status')
    params.set('page', '1')
    setSearchParams(params)
  }

  const changePage = (newPage) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', newPage.toString())
    setSearchParams(params)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          <p className="text-gray-500">Gestisci le organizzazioni e i loro centri</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatsCard 
            title="Totale Organizations" 
            value={stats.total} 
            icon={Building}
            color="primary"
          />
          <StatsCard 
            title="Attive" 
            value={stats.byStatus?.active || 0} 
            icon={CheckCircle}
            color="green"
          />
          <StatsCard 
            title="Sospese" 
            value={stats.byStatus?.suspended || 0} 
            icon={Pause}
            color="amber"
          />
          <StatsCard 
            title="In Cancellazione" 
            value={stats.byStatus?.pending_deletion || 0} 
            icon={AlertCircle}
            color="blue"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 sm:gap-4">
          {/* Search */}
          <div className="flex-1 min-w-full sm:min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca per nome, P.IVA, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Status filter */}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="active">Attive</option>
            <option value="suspended">Sospese</option>
            <option value="pending_deletion">In Cancellazione</option>
          </select>

          <button
            type="submit"
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtra
          </button>
        </form>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : organizations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Building className="h-12 w-12 mb-4 text-gray-300" />
            <p>Nessuna organization trovata</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Responsabile
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Centri
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    P.IVA
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Creata
                  </th>
                  <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {organizations.map((org) => (
                  <tr key={org._id} className="hover:bg-gray-50">
                    <td className="px-3 md:px-6 py-4">
                      <div className="min-w-0 max-w-[160px] sm:max-w-none">
                        <p className="font-medium text-gray-900 truncate">{org.name}</p>
                        <p className="text-sm text-gray-500 truncate">{org.contacts?.email}</p>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4">
                      {org.responsabileId ? (
                        <div className="min-w-0 max-w-[160px] sm:max-w-none">
                          <p className="text-sm text-gray-900 truncate">
                            {org.responsabileId.nome} {org.responsabileId.cognome}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{org.responsabileId.email}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={org.status} />
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm">
                        <Users className="h-4 w-4 mr-1 text-gray-400" />
                        <span className="font-medium">{org.centersCount || 0}</span>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {org.fiscal?.partitaIva || '-'}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(org.createdAt).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right">
                      <Link
                        to={`/organizations/${org._id}`}
                        className="inline-flex items-center px-3 py-1 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Dettagli</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-3 md:px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2">
            <p className="text-xs sm:text-sm text-gray-500">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} totali)
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => changePage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => changePage(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Top Organizations */}
      {stats?.topOrganizations && stats.topOrganizations.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Top Organizations per Centri
          </h2>
          <div className="space-y-3">
            {stats.topOrganizations.slice(0, 5).map((org, index) => (
              <div key={org._id} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-medium">
                    {index + 1}
                  </span>
                  <span className="font-medium text-gray-900 truncate">{org.name}</span>
                  <span className="flex-shrink-0"><StatusBadge status={org.status} /></span>
                </div>
                <span className="flex-shrink-0 text-xs sm:text-sm text-gray-500">
                  {org.centersCount} centri
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
